import { forwardRef, useState, type ReactNode } from "react";
import { Popover as PopoverPrimitive } from "radix-ui";
import classNames from "classnames";
import styles from "./DropdownField.module.scss";
import { SearchTrigger } from "../searchTrigger/SearchTrigger";

export type DropdownFieldProps = {
	/** Field caption. Acts as the placeholder at rest, floats to the top once open or filled. */
	label: string;
	/** Hint shown in place of the value while the dropdown is open and nothing is selected yet. */
	placeholder?: string;
	/** Resolved value displayed once the user has made a selection. */
	value?: string;
	/** Dropdown panel content — anything (a form, a list, counters…). */
	children: ReactNode;
	/** Open state in controlled mode. */
	open?: boolean;
	/** Initial state in uncontrolled mode. */
	defaultOpen?: boolean;
	/** Callback fired on every open/close change. */
	onOpenChange?: (open: boolean) => void;
	disabled?: boolean;
	error?: boolean;
	/** Dropdown panel horizontal alignment relative to the field. */
	align?: "start" | "center" | "end";
	/** Dropdown panel side relative to the field. */
	side?: "top" | "right" | "bottom" | "left";
	/** When false, keeps the chosen `side` instead of flipping when space is tight. */
	avoidCollisions?: boolean;
	className?: string;
	contentClassName?: string;
	id?: string;
};

export const DropdownField = forwardRef<HTMLButtonElement, DropdownFieldProps>(
	(
		{
			label,
			placeholder,
			value,
			children,
			open: controlledOpen,
			defaultOpen = false,
			onOpenChange,
			disabled = false,
			error = false,
			align = "start",
			side = "bottom",
			avoidCollisions,
			className,
			contentClassName,
			id,
		},
		ref
	) => {
		const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
		const isControlled = controlledOpen !== undefined;
		const open = isControlled ? controlledOpen : uncontrolledOpen;

		const handleOpenChange = (nextOpen: boolean) => {
			if (!isControlled) {
				setUncontrolledOpen(nextOpen);
			}
			onOpenChange?.(nextOpen);
		};

		return (
			<PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
				<PopoverPrimitive.Trigger asChild disabled={disabled}>
					<SearchTrigger
						ref={ref}
						id={id}
						label={label}
						placeholder={placeholder}
						value={value}
						error={error}
						isOpen={open}
						className={className}
					/>
				</PopoverPrimitive.Trigger>

				<PopoverPrimitive.Portal>
					<PopoverPrimitive.Content
						align={align}
						side={side}
						avoidCollisions={avoidCollisions}
						sideOffset={4}
						className={classNames(styles["dropdown-field__content"], contentClassName)}
					>
						{children}
					</PopoverPrimitive.Content>
				</PopoverPrimitive.Portal>
			</PopoverPrimitive.Root>
		);
	}
);

DropdownField.displayName = "DropdownField";
